import { Box, Group, MantineTheme, Stack, Text } from '@mantine/core'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getEntities } from 'app/state/ducks/entities/selectors'
import {
  formatNumberToCurrency,
  mapEntityIdsToNames,
} from 'app/utils/util-functions'
import cleanUUID from 'app/views/components/functions/cleanUUID'
import { formatHistoryDate } from './facility-history-columns'
import {
  FacilityHistoryEntry,
  FacilityHistoryFieldChange,
  FacilityHistoryLenderChange,
} from './facility-history-types'

// Only a fallback for rows that pre-date the feed carrying a currency; every
// live entry supplies its own — facility-level figures in the facility's base
// currency, loan-level figures in the loan's.
const DEFAULT_CURRENCY = 'USD'

function formatFieldValue(
  raw: string | null,
  kind: FacilityHistoryFieldChange['kind'],
  currency?: string,
): string {
  if (raw === null || raw === undefined || raw === '') return '—'
  if (kind === 'currency') {
    const n = Number(raw)
    if (!Number.isFinite(n)) return raw
    return formatNumberToCurrency(n, currency ?? DEFAULT_CURRENCY, 2, 2)
  }
  if (kind === 'percent') {
    if (/%\s*$/.test(raw)) return raw
    const n = Number(raw)
    if (!Number.isFinite(n)) return raw
    return `${n}%`
  }
  return raw
}

function formatLenderAmount(raw: string | null, currency?: string): string {
  if (raw === null || raw === undefined || raw === '') return '—'
  const n = Number(raw)
  if (!Number.isFinite(n)) return raw
  return formatNumberToCurrency(n, currency ?? DEFAULT_CURRENCY, 2, 2)
}

interface Props {
  entry: FacilityHistoryEntry
}

function fieldColor(kind: FacilityHistoryFieldChange['kind']): string {
  switch (kind) {
    case 'currency':
    case 'number':
      return '#16A34A'
    case 'percent':
      return '#2563EB'
    case 'date':
      return '#9333EA'
    default:
      return '#374151'
  }
}

function FieldDiff({
  change,
  currency,
}: {
  change: FacilityHistoryFieldChange
  currency?: string
}) {
  const color = fieldColor(change.kind)
  return (
    <Stack spacing={2}>
      <Group spacing={6} noWrap>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: 999,
            backgroundColor: '#6366F1',
            flexShrink: 0,
          }}
        />
        <Text fz={16} fw={600} c="#374151">
          {change.field}
        </Text>
      </Group>
      <Group spacing={6} noWrap ml={12}>
        <Text fz={14} c="dimmed">
          {formatFieldValue(change.before, change.kind, currency)}
        </Text>
        <Text fz={14} c="dimmed">
          →
        </Text>
        <Text fz={16} fw={600} sx={{ color }}>
          {formatFieldValue(change.after, change.kind, currency)}
        </Text>
      </Group>
    </Stack>
  )
}

function LenderDiff({
  change,
  lenderName,
  currency,
}: {
  change: FacilityHistoryLenderChange
  lenderName: string
  currency?: string
}) {
  return (
    <Stack spacing={2}>
      <Group spacing={6} noWrap>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: 999,
            backgroundColor: '#6366F1',
            flexShrink: 0,
          }}
        />
        <Text fz={16} fw={600} c="#374151">
          {lenderName}
        </Text>
      </Group>
      <Group spacing={6} noWrap ml={12}>
        <Text fz={14} c="dimmed">
          {formatLenderAmount(change.before, currency)}
        </Text>
        <Text fz={14} c="dimmed">
          →
        </Text>
        <Text fz={16} fw={600} sx={{ color: '#16A34A' }}>
          {formatLenderAmount(change.after, currency)}
        </Text>
      </Group>
    </Stack>
  )
}

export default function FacilityHistoryModifiedFields({ entry }: Props) {
  const entities = useSelector(getEntities)
  const entityIdToName = useMemo(
    () => mapEntityIdsToNames(entities),
    [entities],
  )

  const hasFields = entry.modifiedFields.length > 0
  const hasLenders = !!entry.modifiedLenders?.length
  const hasLoans = !!entry.modifiedLoans?.length

  const revertBox = entry.revertReason ? (
    <Box
      w="100%"
      mb="sm"
      p="sm"
      sx={(theme: MantineTheme) => ({
        border: `1px solid ${theme.colors.red[6]}`,
        backgroundColor: theme.colors.red[0],
        borderRadius: theme.radius.sm,
      })}
    >
      <Text fz={16} fw={600} c="red.7">
        Revert reason
      </Text>
      <Text fz={14} c="red.9">
        {entry.revertReason}
      </Text>
    </Box>
  ) : null

  // A reverse has been drafted but not yet approved, so the action below still
  // stands. Shown above the diffs rather than replacing them, unlike an applied
  // revert.
  const pendingRevertBanner = entry.openRevert ? (
    <Box
      w="100%"
      mb="sm"
      p="sm"
      sx={(theme: MantineTheme) => ({
        border: `1px solid ${theme.colors.yellow[5]}`,
        backgroundColor: theme.colors.yellow[0],
        borderRadius: theme.radius.sm,
      })}
    >
      <Text fz={16} fw={600} c="yellow.8">
        Reverse submitted
      </Text>
      <Text fz={14} c="yellow.9">
        A reversal has been submitted for this transaction and is awaiting
        manager approval.
      </Text>
      {entry.openRevert.reason ? (
        <Text fz={14} c="yellow.9">
          With the message: &quot;{entry.openRevert.reason}&quot;
        </Text>
      ) : null}
    </Box>
  ) : null

  // The processing blocker, and every action it stands in front of. Explains in
  // full what the greyed-out reverse control in the row can only hint at.
  const revertBlockedBanner = entry.revertBlockedBefore ? (
    <Box
      w="100%"
      mb="sm"
      p="sm"
      sx={(theme: MantineTheme) => ({
        border: `1px solid ${theme.colors.gray[5]}`,
        backgroundColor: theme.colors.gray[0],
        borderRadius: theme.radius.sm,
      })}
    >
      <Text fz={16} fw={600} c="gray.8">
        Reverse unavailable
      </Text>
      <Text fz={14} c="gray.8">
        Transactions with an effective date on or before{' '}
        {formatHistoryDate(entry.revertBlockedBefore)} can&apos;t be
        reverted — later activity has already been posted against them, and
        reverting would leave the position out of sync. To correct this, post an
        adjusting transaction with a current effective date.
      </Text>
    </Box>
  ) : null

  // Reverted entries show only the revert reason — the field/loan/lender diffs
  // belong to the original action and would be misleading next to the reversal.
  if (entry.revertReason) {
    return (
      <Box
        w="100%"
        sx={{
          backgroundColor: '#f8f9fa',
          padding: '12px 16px',
          borderTop: '1px solid #E5E7EB',
        }}
      >
        {revertBox}
      </Box>
    )
  }

  if (!hasFields && !hasLenders && !hasLoans) {
    return (
      <Box
        w="100%"
        sx={{
          backgroundColor: '#f8f9fa',
          padding: '12px 16px',
          borderTop: '1px solid #E5E7EB',
        }}
      >
        {pendingRevertBanner}
        {revertBlockedBanner}
        {revertBlockedBanner ? null : (
          <Text fz={14} c="dimmed">
            No field-level details available for this transaction.
          </Text>
        )}
      </Box>
    )
  }

  return (
    <Box
      w="100%"
      sx={{
        backgroundColor: '#f8f9fa',
        padding: '12px 16px',
        borderTop: '1px solid #E5E7EB',
      }}
    >
      {pendingRevertBanner}
      {revertBlockedBanner}
      {hasFields ? (
        <>
          <Text
            fz={18}
            fw={700}
            c="#6B7280"
            mb={8}
            tt="uppercase"
            ta="left"
            sx={{ width: '100%', alignSelf: 'flex-start' }}
          >
            Modified Fields ({entry.modifiedFields.length})
          </Text>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              columnGap: 32,
              rowGap: 12,
            }}
          >
            {entry.modifiedFields.map((change, idx) => (
              <Box
                key={`${entry.id}-field-${change.field}-${idx}`}
                sx={{ flex: '0 0 auto' }}
              >
                <FieldDiff change={change} currency={entry.currency} />
              </Box>
            ))}
          </Box>
        </>
      ) : null}
      {hasLenders ? (
        <>
          <Text
            fz={18}
            fw={700}
            c="#6B7280"
            mb={8}
            mt={hasFields ? 16 : 0}
            tt="uppercase"
            ta="left"
            sx={{ width: '100%', alignSelf: 'flex-start' }}
          >
            Modified Lender Positions ({entry.modifiedLenders!.length})
          </Text>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              columnGap: 32,
              rowGap: 12,
            }}
          >
            {entry.modifiedLenders!.map((change, idx) => {
              const lenderName =
                entityIdToName.get(cleanUUID(change.lenderId)) ??
                entityIdToName.get(change.lenderId) ??
                change.lenderId
              return (
                <Box
                  key={`${entry.id}-lender-${change.lenderId}-${idx}`}
                  sx={{ flex: '0 0 auto' }}
                >
                  <LenderDiff
                    change={change}
                    lenderName={lenderName}
                    currency={entry.currency}
                  />
                </Box>
              )
            })}
          </Box>
        </>
      ) : null}
      {hasLoans ? (
        <>
          <Text
            fz={18}
            fw={700}
            c="#6B7280"
            mb={8}
            mt={hasFields || hasLenders ? 16 : 0}
            tt="uppercase"
            ta="left"
            sx={{ width: '100%', alignSelf: 'flex-start' }}
          >
            Modified Loans ({entry.modifiedLoans!.length})
          </Text>
          <Stack spacing={12} w="100%" mb={8}>
            {entry.modifiedLoans!.map(loanDiff => (
              <Box
                key={`${entry.id}-loan-${loanDiff.loanId}`}
                sx={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid #E5E7EB',
                  backgroundColor: 'white',
                }}
              >
                <Text fz={16} fw={600} c="#374151" mb={6}>
                  {loanDiff.loanLabel}
                </Text>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    columnGap: 32,
                    rowGap: 12,
                  }}
                >
                  {loanDiff.modifiedFields.map((change, idx) => (
                    <Box
                      key={`${entry.id}-loan-${loanDiff.loanId}-field-${change.field}-${idx}`}
                      sx={{ flex: '0 0 auto' }}
                    >
                      <FieldDiff
                        change={change}
                        currency={loanDiff.currency ?? entry.currency}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  )
}
